import React, {useEffect, useState} from 'react';
import DateRangePicker from '../../common/DateRangePicker';
import {API} from "aws-amplify";
import {toast} from "react-toastify";

const AddEditSatisfactionFilters = ({
                                        onSaveFilters, onCancel, onResetFilter, shifts, filters,
                                        lastDaysInput, startDate, endDate, shiftId, currentSort
                                    }) => {
    const [reset, setReset] = useState(false);
    const [shift, setShift] = useState('Select');
    const [role, setRole] = useState({
        callTaker: false,
        investigator: false,
        responder: false,
    });
    const [value, setValue] = useState({
        startDate: null,
        endDate: null,
    });
    const [email, setEmail] = useState(false);
    const [dateOption, setDateOption] = useState('');
    const [lastDays, setLastDays] = useState('');
    const [originalShift, setOriginalShift] = useState('Select');
    const [originalRole, setOriginalRole] = useState({
        callTaker: false,
        investigator: false,
        responder: false,
    });
    const [originalEmail, setOriginalEmail] = useState(false);
    const [originalDateOption, setOriginalDateOption] = useState('');
    const [originalLastDays, setOriginalLastDays] = useState('');
    const [originalDateRange, setOriginalDateRange] = useState({
        startDate: null,
        endDate: null,
    });

    const toLocalDate = (dateString) => {
        const [year, month, day] = dateString.split('-');
        return new Date(year, month - 1, day);
    };

    const fetchFilterSettings = async () => {
        try {
            const response = await API.get('Core', '/api/v1/satisfaction/filter');
            const {filters, lastDays, startDate, endDate, shiftId} = response;
            const initialRole = {
                callTaker: filters.includes("CALLTAKER"),
                investigator: filters.includes("INVESTIGATOR"),
                responder: filters.includes("RESPONDER"),
            };
            const initialEmail = filters.includes("HAS_EMAIL");
            const computedDateOption = lastDays ? 'lastInput' : (startDate && endDate) ? 'customRange' : '';
            const computedLastDays = lastDays || '';
            const initialDates = {
                startDate: startDate ? toLocalDate(startDate) : null,
                endDate: endDate ? toLocalDate(endDate) : null,
            };

            setRole(initialRole);
            setOriginalRole(initialRole);

            const fetchedShift = shifts.find(s => s.id === shiftId)?.name || 'Select';
            setShift(fetchedShift);
            setOriginalShift(fetchedShift);

            setEmail(initialEmail);
            setOriginalEmail(initialEmail);

            setDateOption(computedDateOption);
            setOriginalDateOption(computedDateOption);

            setLastDays(computedLastDays);
            setOriginalLastDays(computedLastDays);

            setValue(initialDates);
            setOriginalDateRange(initialDates);
        } catch (error) {
            toast.error(`Failed to fetch filter settings: ${error.response?.data?.message || error.message}`, {
                position: "top-center",
            });
        }
    };

    useEffect(() => {
        fetchFilterSettings();
    }, []);

    const handleResetShift = () => setShift(originalShift);
    const handleResetRole = () => setRole(originalRole);
    const handleResetEmail = () => setEmail(originalEmail);
    const handleResetDate = () => {
        setDateOption(originalDateOption);
        setValue(originalDateRange);
        setLastDays(originalLastDays);
    };

    const handleResetAllFilters = () => {
        setReset(true);
        setShift('Select');
        setRole({
            callTaker: false,
            investigator: false,
            responder: false,
        });
        setEmail(false);
        setDateOption('');
        setLastDays('');
        setValue({startDate: null, endDate: null});
        onResetFilter();
    };

    const validateFilters = () => {
        const showToast = (message) => {
            toast.error(message, {
                position: "top-center"
            });
        };
        if (!dateOption) {
            showToast("Please select a date option.");
            return false;
        }
        const notValidLastDays = !/^[1-9]\d*$/.test(lastDays);
        const isValidCustomRange = value.startDate && value.endDate;
        if (dateOption === 'lastInput') {
            if (notValidLastDays) {
                showToast("Last Days must be a valid integer.");
                return false;
            }
        }
        if (dateOption === 'customRange') {
            if (!isValidCustomRange) {
                showToast("Please specify both start & end dates for custom date range.");
                return false;
            }
        }
        return true;
    };

    const handleApplyFilters = () => {
        if (!validateFilters()) return;
        const filters = [];
        if (role.callTaker) filters.push("CALLTAKER");
        if (role.investigator) filters.push("INVESTIGATOR");
        if (role.responder) filters.push("RESPONDER");
        if (email) filters.push("HAS_EMAIL");
        const prepareLocalDateString = date => {
            if (!date) return null;
            const localDate = new Date(date);
            localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
            return localDate.toISOString().split('T')[0];
        };
        const requestBody = {
            filters: filters.join(','),
            lastDays: dateOption === 'customRange' ? null : (lastDays ? +lastDays : null),
            startDate: dateOption === 'lastInput' ? null : prepareLocalDateString(value.startDate),
            endDate: dateOption === 'lastInput' ? null : prepareLocalDateString(value.endDate),
            shiftId: shifts.find(s => s.name === shift)?.id || null,
            sorting: currentSort
        };
        API.put("Core", "/api/v1/satisfaction/filter", {body: requestBody})
            .then(response => {
                toast.success("Filters applied successfully!", {
                    position: "top-center"
                });
                onSaveFilters();
            })
            .catch(error => {
                toast.error("Could not apply filters: " + error.response.data.message, {
                    position: "top-center"
                });
            });
    };

    const hasFilterChanged = () => {
        if (reset) return false;
        const getComparableDate = (date) => (date ? date.toISOString().split('T')[0] : '');
        const hasShiftChanged = shift !== (shifts.find(s => s.id === shiftId)?.name || 'Select');
        const hasRoleChanged =
            role.callTaker !== filters.includes("CALLTAKER") ||
            role.investigator !== filters.includes("INVESTIGATOR") ||
            role.responder !== filters.includes("RESPONDER");

        const hasEmailChanged = email !== filters.includes("HAS_EMAIL");

        const hasLastDaysChanged = lastDays !== (lastDaysInput || '');
        const hasDateChanged = (
            getComparableDate(value.startDate) !== startDate ||
            getComparableDate(value.endDate) !== endDate
        );

        return (
            hasShiftChanged ||
            hasRoleChanged ||
            hasEmailChanged ||
            hasLastDaysChanged ||
            hasDateChanged
        );
    };

    const isAnyFilterSelected = () => {
        return (
            shift !== 'Select' ||
            role.callTaker ||
            role.investigator ||
            role.responder ||
            email ||
            lastDays !== '' ||
            (value.startDate !== null && value.endDate !== null)
        );
    };
    return (
        <div style={{padding: '15px'}}>
            <div style={{marginBottom: '15px'}} className="inputText">Fields marked with <span
                style={{color: 'red'}}>*</span> are required.
            </div>
            <div style={{marginBottom: '20px'}}>
                <div className="filterAlignView">
                    <label className="inputText">SHIFT</label>
                    <button
                        className="button dismiss small reset"
                        onClick={handleResetShift}
                        disabled={shift === originalShift}
                        aria-label="resetShift"
                    >
                        Reset
                    </button>
                </div>
                <div className="inputText">
                    <select
                        value={shift}
                        onChange={(e) => setShift(e.target.value)}
                        style={{width: '100%', height: '30px', marginTop: '10px', color: 'var(--colorTitle)'}}
                        className="tableCell titledSelectCell"
                        aria-label="Shifts"
                    >
                        <option aria-label="Select Shift" className="inputText tableCell titledSelectCell"
                                value="Select">Select
                        </option>
                        {shifts.map(({id, name}) => (
                            <option key={id} value={name} aria-label={`${name} shift`}
                                    className="inputText tableCell titledSelectCell">
                                {name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="separator" style={{borderTop: '1px solid #ccc', margin: '10px 0'}}></div>
            <fieldset style={{marginBottom: '20px', position: 'relative'}}>
                <legend className="filterAlignView inputText">ROLE</legend>
                <button
                    className="button dismiss small reset"
                    style={{position: 'absolute', right: 0, transform: 'translateY(-75%)'}}
                    onClick={handleResetRole}
                    disabled={JSON.stringify(role) === JSON.stringify(originalRole)}
                    aria-label="resetRole"
                >
                    Reset
                </button>
                <div style={{marginTop: '10px'}} className="inputText">
                    <input
                        type="checkbox"
                        checked={role.callTaker}
                        onChange={() => setRole({...role, callTaker: !role.callTaker})}
                        aria-label="Call Takers"
                    /> Call Takers
                </div>
                <div className="inputText">
                    <input
                        type="checkbox"
                        checked={role.investigator}
                        onChange={() => setRole({...role, investigator: !role.investigator})}
                        aria-label="Investigators"
                    /> Investigators
                </div>
                <div className="inputText">
                    <input
                        type="checkbox"
                        checked={role.responder}
                        onChange={() => setRole({...role, responder: !role.responder})}
                        aria-label="Responders"
                    /> Responders
                </div>
            </fieldset>
            <div className="separator" style={{borderTop: '1px solid #ccc', margin: '10px 0'}}></div>
            <fieldset style={{marginBottom: '20px', position: 'relative'}}>
                <legend className="filterAlignView inputText">EMAIL</legend>
                <button
                    className="button dismiss small reset"
                    onClick={handleResetEmail}
                    disabled={email === originalEmail}
                    style={{position: 'absolute', right: 0, transform: 'translateY(-75%)'}}
                    aria-label="resetEmail"
                >
                    Reset
                </button>
                <div style={{marginTop: '10px'}} className="inputText">
                    <input
                        type="checkbox"
                        checked={email}
                        onChange={() => setEmail(!email)}
                        aria-label="Has Email"
                    /> Has Email
                </div>
            </fieldset>
            <div className="separator" style={{borderTop: '1px solid #ccc', margin: '10px 0'}}></div>
            <fieldset aria-required="true" style={{marginBottom: '20px', position: 'relative'}}>
                <legend className="filterAlignView inputText">DATE<span style={{color: 'red'}}>*</span></legend>
                <button
                    className="button dismiss small reset"
                    onClick={handleResetDate}
                    disabled={
                        dateOption === originalDateOption &&
                        lastDays === originalLastDays &&
                        JSON.stringify(value) === JSON.stringify(originalDateRange)
                    }
                    style={{position: 'absolute', right: 0, transform: 'translateY(-75%)'}}
                    aria-label="resetDate"
                >
                    Reset
                </button>
                <div style={{marginTop: '10px'}} className="inputText">
                    <input
                        type="radio"
                        checked={dateOption === 'lastInput'}
                        onChange={() => setDateOption('lastInput')}
                        aria-label="Last Days"
                    /> Last <input
                    type="text"
                    aria-label="Enter No of Days"
                    className="lastDaysInput"
                    value={dateOption === 'lastInput' ? lastDays : ''}
                    onChange={(e) => setLastDays(e.target.value)}
                    disabled={dateOption !== 'lastInput'}
                /> Days
                </div>
                <div className="inputText">
                    <input
                        type="radio"
                        checked={dateOption === 'customRange'}
                        onChange={() => setDateOption('customRange')}
                        aria-label="Custom Range"
                    />
                    Custom Range
                </div>
                {dateOption === 'customRange' && (
                    <DateRangePicker
                        startDate={value.startDate}
                        endDate={value.endDate}
                        startDateAriaLabel='Start date'
                        endDateAriaLabel='End date'
                        onDateChange={(dates) => {
                            const [start, end] = dates;
                            setValue({startDate: start, endDate: end});
                        }}
                    />
                )}
            </fieldset>
            <div className="filterButtonView">
                <button
                    onClick={handleApplyFilters}
                    className={hasFilterChanged() ? "button confirm small inputText" : "button small disabled dismiss inputText"}
                    disabled={!hasFilterChanged()}>
                    Apply
                </button>
                <button style={{marginRight: '35px'}} onClick={onCancel}
                        className="button dismiss small inputText">
                    Cancel
                </button>
                <button style={{marginLeft: '5px', whiteSpace:'nowrap'}} onClick={handleResetAllFilters}
                        className={isAnyFilterSelected() ? "button tint small inputText" : "button small disabled dismiss inputText"}
                        disabled={!isAnyFilterSelected()}>
                    Reset All Filters
                </button>
            </div>
        </div>
    );
};

export default AddEditSatisfactionFilters;